import React from 'react';
import StaticPageLayout from '../components/StaticPageLayout';

const SUPPORT_EMAIL = 'support@todaytheapp.com';
const LAST_UPDATED = 'April 17, 2026';

export default function PrivacyPolicyPage() {
  return (
    <StaticPageLayout testId="privacy-policy-page">
      <div className="max-w-3xl mx-auto px-6 sm:px-8 py-12 sm:py-16">
        <p className="text-xs uppercase tracking-widest font-semibold mb-3" style={{ color: 'var(--primary)', fontFamily: 'Manrope' }}>
          Legal
        </p>
        <h1 className="text-4xl sm:text-5xl font-bold mb-3 leading-tight" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>
          Privacy Policy
        </h1>
        <p className="text-sm mb-10" style={{ color: 'var(--muted-fg)' }}>
          Last updated: {LAST_UPDATED}
        </p>

        <div className="space-y-8" style={{ color: 'var(--text)', fontFamily: 'Georgia, serif', fontSize: '16px', lineHeight: 1.8 }}>
          <p>
            Today: Daily Reset ("<strong>Today</strong>", "we", "us") is committed to protecting your privacy. This policy explains what data we collect, how we use it, who we share it with, and the rights you have over it. By using our web app or mobile app, you agree to the practices described here.
          </p>

          <Section title="1. Information we collect">
            <p className="mb-3"><strong>Account data:</strong> name, email address, profile picture (if you sign in with Google), and a device identifier generated in your browser.</p>
            <p className="mb-3"><strong>Wellness content you create:</strong> mood entries, gratitude journal entries, doodles (rendered on your device, not sent to servers unless you save them), personality test answers and results, program progress, meal preferences.</p>
            <p className="mb-3"><strong>Usage data:</strong> pages and features you visit, interaction events, approximate location derived from your IP address for currency detection only, device/browser type, and timezone.</p>
            <p><strong>Payment data:</strong> when you purchase a subscription or report, payment details (card number, UPI, wallet) are collected and processed <em>directly by our payment processor, Razorpay</em>. We never see or store your full card information. We store only: transaction ID, amount, currency, status, and the product you purchased.</p>
          </Section>

          <Section title="2. How we use your information">
            <ul className="list-disc pl-5 space-y-1.5">
              <li>To provide, personalize, and improve the Today app experience.</li>
              <li>To fulfill purchases and unlock paid features tied to your account.</li>
              <li>To generate your personalized personality and IQ reports (via a large language model, described below).</li>
              <li>To send transactional communications (receipts, password resets, account changes).</li>
              <li>To detect abuse, fraud, and security incidents.</li>
              <li>To comply with legal obligations.</li>
            </ul>
          </Section>

          <Section title="3. Legal basis for processing (EEA / UK users)">
            <p>We rely on the following legal bases under the GDPR / UK GDPR: (a) performance of our contract with you, (b) your consent (which you can withdraw at any time), (c) our legitimate interests in operating and improving the service, and (d) compliance with legal obligations.</p>
          </Section>

          <Section title="4. Third-party processors">
            <p className="mb-3">We share limited, purpose-specific data with these processors, each bound by strict contractual and data-protection obligations:</p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li><strong>Razorpay</strong> — payment processing, fraud prevention.</li>
              <li><strong>Google Firebase / Google Analytics (GA4)</strong> — anonymous usage analytics and crash reporting.</li>
              <li><strong>Meta Pixel (Facebook)</strong> — conversion analytics. You can disable this with any standard ad-blocker or browser tracking protection.</li>
              <li><strong>Google Sign-In</strong> — authentication (when you choose that option).</li>
              <li><strong>OpenAI / Anthropic / Google (LLM providers)</strong> — to generate your personality and IQ reports. We send only the anonymized test responses needed to produce the report; no identifying information is included.</li>
              <li><strong>Cloud hosting providers</strong> — to run the app infrastructure.</li>
            </ul>
          </Section>

          <Section title="5. Cookies and similar technologies">
            <p>We use first-party cookies and browser localStorage to keep you signed in, remember preferences, and maintain a stable device identifier. Analytics partners (GA4, Meta Pixel) may set their own cookies. You can control or delete cookies via your browser settings; doing so may affect functionality.</p>
          </Section>

          <Section title="6. Data retention">
            <p>We retain account data and wellness content for as long as your account is active. If you delete your account, we remove your personal data within 30 days, except where we must keep it to comply with legal, tax, or accounting obligations (typically up to 7 years for transaction records).</p>
          </Section>

          <Section title="7. Your rights">
            <p className="mb-3">Depending on your jurisdiction, you may have the right to:</p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li>Access, correct, or delete the personal data we hold about you.</li>
              <li>Port your data to another service.</li>
              <li>Object to or restrict certain processing.</li>
              <li>Withdraw consent at any time.</li>
              <li>Lodge a complaint with your local data protection authority.</li>
            </ul>
            <p className="mt-3">
              To exercise any of these rights, email us at <a href={`mailto:${SUPPORT_EMAIL}`} className="underline" style={{ color: 'var(--primary)' }}>{SUPPORT_EMAIL}</a>.
            </p>
          </Section>

          <Section title="8. Security">
            <p>We use industry-standard safeguards — HTTPS in transit, encryption at rest, access controls, and regular audits. No service is ever perfectly secure; if you believe your account has been compromised, contact us immediately.</p>
          </Section>

          <Section title="9. Children's privacy">
            <p>Today is not directed at children under 13 (or under 16 in the EEA). We do not knowingly collect personal data from children. If you believe a child has provided us information, please contact us and we will delete it.</p>
          </Section>

          <Section title="10. International transfers">
            <p>Your data may be processed in countries other than your own (including the United States and India). Where required, we use Standard Contractual Clauses or equivalent safeguards to protect cross-border transfers.</p>
          </Section>

          <Section title="11. Changes to this policy">
            <p>We'll update this page if our practices change materially and notify you in-app or by email. The "Last updated" date at the top always reflects the current version.</p>
          </Section>

          <Section title="12. Contact us">
            <p>
              Questions, requests, or concerns about privacy? Email{' '}
              <a href={`mailto:${SUPPORT_EMAIL}`} className="underline" style={{ color: 'var(--primary)' }}>{SUPPORT_EMAIL}</a>. We aim to respond within 48 hours on business days.
            </p>
          </Section>
        </div>
      </div>
    </StaticPageLayout>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="text-xl font-bold mb-3" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>
        {title}
      </h2>
      <div>{children}</div>
    </section>
  );
}
